import React from 'react';
import PropTypes from 'prop-types';
import removeKeys from '../utils/removeKeys';
import { resolveTestId } from '@cvent/nucleus-test-automation';


/**
 * Keeps track of the stack of focus traps.
 */
let focusTrapStack = [];

/**
 * FocusTrap - A container to maintain browser focus. When tabbing through
 * elements or otherwise changing focus, you will be unable to focus onto
 * any elements outside of the FocusTrap. Mounting a new FocusTrap will put
 * it on top of the 'stack', maintaining focus within it. When that FocusTrap
 * is unmounted, focus will return to the next element on the stack.
 */
export default class FocusTrap extends React.Component {
  static displayName = 'FocusTrap';
  static propTypes = {
    /** The child content for the container.  */
    children: PropTypes.node,
    /** Flag indicating whether to focus the container when it is mounted.  */
    focusOnMount: PropTypes.bool
  };
  static defaultProps = {
    focusOnMount: true,
    tabIndex: 0
  };
  constructor(props) {
    super(props);
    this.state = {
      anchor: document && document.activeElement ? document.activeElement : null
    };
    this.onBlur = this.onBlur.bind(this);
    this.returnFocus = this.returnFocus.bind(this);
    this.trapFocus = this.trapFocus.bind(this);
    this.contains = this.contains.bind(this);
    this.focus = this.focus.bind(this);
  }

  componentDidMount() {
    focusTrapStack.push(this);
    if (this.props.focusOnMount) {
      this.trapFocus();
    }
    if (document && document.addEventListener) {
      document.addEventListener('focus', this.onBlur, true);
    }
  }

  componentWillUnmount() {
    focusTrapStack = focusTrapStack.filter(element => element !== this);
    if (document && document.removeEventListener) {
      document.removeEventListener('focus', this.onBlur, true);
    }
    this.returnFocus();
  }

  onBlur(event) {
    const current = focusTrapStack[focusTrapStack.length - 1];
    if (current && current.contains(event.target) === false) {
      event.preventDefault();
      this.trapFocus();
    }
  }

  returnFocus() {
    if (this.state.anchor &&
        typeof this.state.anchor === 'object' &&
        typeof this.state.anchor.focus === 'function') {
      this.state.anchor.focus();
    }
  }

  trapFocus() {
    const element = focusTrapStack[focusTrapStack.length - 1];
    if (element &&
        typeof element === 'object' &&
        typeof element.focus === 'function') {
      element.focus();
    }
  }

  contains(element) {
    return this.root && this.root.contains(element);
  }

  focus() {
    if (this.root) {
      return this.root.focus();
    }
  }

  render() {
    const props = removeKeys(this.props, ['focusOnMount', 'classes']);
    return (
      <div
        {...resolveTestId(this.props)}
        {...props}
        style={{ outline: 'none' }}
        ref={(c) => this.root = c }
      />
    );
  }
}
